import { createSlice } from '@reduxjs/toolkit';

/**
 * @typedef { {
 * type: string,
 * jobid: string,
 * jobtype: import('BMapsSrc/jobman').JobTypeOptions,
 * state: import('BMapsSrc/jobman').JobStatusOptions,
 * done: string, delivered: string, created: string, modified: string,
 * requestmeta: { [key: string]: any },
 * } } Job
 * @type { {
 * jobs: Job[],
 * status: 'unloaded' | 'loading' | 'loaded',
 * reachability: 'unknown' | 'ok' | 'unreachable' | 'no-login',
 * availableTypes: string[],
 * localJobState:  { [string]: { [key: string]: any } },
 * displayedCompoundJobs: string[],
 * } }
 *
 */
export const initialState = {
    jobs: [],
    status: 'unloaded',
    // Reachability, which is not load progress: what the last answer said about the service.
    // 'ok' once it answered (a poll or a job it accepted), 'unreachable' after a failed
    // request, 'no-login' when a poll found no login, and 'unknown' until any of those.
    reachability: 'unknown',
    availableTypes: ['folding'],
    localJobState: {},
    displayedCompoundJobs: [],
    displayedScreeningJobs: [],
    displayedFreeEnergyJobs: [],
    displayedMdJobs: [],
};

const slice = createSlice({
    name: 'jobs',
    initialState,
    reducers: {
        loadJobs: (state, action) => {
            state.jobs = action.payload; state.status = 'loaded'; state.reachability = 'ok';
        },
        addJob: (state, action) => {
            // A job the service accepted is proof it answered.
            state.jobs = [...state.jobs, action.payload]; state.reachability = 'ok';
        },
        removeJob: (state, action) => {
            state.jobs = state.jobs.filter((job) => job.jobid !== action.payload);
            if (state.localJobState[action.payload]) {
                state.localJobState[action.payload] = undefined;
            }
        },
        setJob: (state, action) => {
            const index = state.jobs.findIndex((job) => job.jobid === action.payload.jobid);
            state.jobs[index] = action.payload;
        },
        updateLocalJobState: (state, action) => {
            const jobId = action.payload.jobid;
            state.localJobState[jobId] = {
                ...state.localJobState[jobId],
                ...action.payload,
            };
        },
        updateDisplayedCompoundJobs: (state, action) => {
            state.displayedCompoundJobs = action.payload;
        },
        updateDisplayedScreeningJobs: (state, action) => {
            state.displayedScreeningJobs = action.payload;
        },
        updateDisplayedFreeEnergyJobs: (state, action) => {
            state.displayedFreeEnergyJobs = action.payload;
        },
        updateDisplayedMdJobs: (state, action) => {
            state.displayedMdJobs = action.payload;
        },
        // The payload is getJobs' failure reason: 'auth' (no login) or 'service'.
        loadJobsFailed: (state, action) => {
            state.reachability = action.payload === 'auth' ? 'no-login' : 'unreachable';
        },
        setJobState: (state, action) => (action.payload),
        reset: () => initialState,
    },
});

export default slice;
